import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { can } from "@/admin/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Inbox, Tag, BarChart3, Users, ScrollText, LogOut, Menu, X, ExternalLink, FileText, Images, HelpCircle, ListChecks,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, key: "dashboard", end: true },
  { to: "/admin/inquiries", label: "Travel Inquiries", icon: Inbox, key: "inquiries" },
  { to: "/admin/subscribers", label: "Travel List", icon: ListChecks, key: "subscribers" },
  { to: "/admin/offers", label: "Travel Offers", icon: Tag, key: "offers" },
  { to: "/admin/homepage", label: "Homepage Content", icon: FileText, key: "content" },
  { to: "/admin/gallery", label: "Travel Gallery", icon: Images, key: "gallery" },
  { to: "/admin/faqs", label: "FAQ Manager", icon: HelpCircle, key: "faqs" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, key: "analytics" },
  { to: "/admin/users", label: "Admin Users", icon: Users, key: "users" },
  { to: "/admin/activity", label: "Activity Log", icon: ScrollText, key: "activity" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => can(user.role, n.key));

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-white/10">
        <p className="font-serif text-2xl font-semibold text-white">KIS Living<span className="text-terracotta">.</span></p>
        <p className="text-white/40 text-xs mt-1">Admin Console</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-terracotta text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <a href="/" target="_blank" rel="noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          <ExternalLink size={18} /> View Website
        </a>
        <div className="px-3 pt-3 pb-1">
          <p className="text-white text-sm font-medium truncate">{user.name}</p>
          <p className="text-white/40 text-xs">{user.role_label}</p>
        </div>
        <Button data-testid="admin-logout" onClick={doLogout} variant="ghost"
          className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/10 rounded-xl">
          <LogOut size={18} /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-ink flex-col fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-ink"><SidebarContent /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-64 min-w-0">
        <header className="md:hidden flex items-center justify-between bg-ink px-4 h-14 sticky top-0 z-40">
          <p className="font-serif text-xl text-white">KIS Living<span className="text-terracotta">.</span></p>
          <button data-testid="admin-mobile-menu" onClick={() => setOpen((o) => !o)} className="text-white">
            {open ? <X /> : <Menu />}
          </button>
        </header>
        <main className="p-5 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
